import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowLeft, Save, UserCog } from "lucide-react";
import { HelpTip } from "../components/HelpTip";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const departments = [
  "Tecnología", "Recursos Humanos", "Finanzas", "Marketing",
  "Ventas", "Operaciones", "Legal", "Administración"
];

const scheduleTemplate = [
  { day: "lunes", label: "Lunes", enabled: true, start_time: "09:00", end_time: "18:00" },
  { day: "martes", label: "Martes", enabled: true, start_time: "09:00", end_time: "18:00" },
  { day: "miercoles", label: "Miércoles", enabled: true, start_time: "09:00", end_time: "18:00" },
  { day: "jueves", label: "Jueves", enabled: true, start_time: "09:00", end_time: "18:00" },
  { day: "viernes", label: "Viernes", enabled: true, start_time: "09:00", end_time: "18:00" },
  { day: "sabado", label: "Sábado", enabled: false, start_time: "", end_time: "" },
  { day: "domingo", label: "Domingo", enabled: false, start_time: "", end_time: "" },
];

const emptyBanking = { payment_method: "transferencia", bank_name: "", card_number: "", account_number: "", clabe: "" };
const emptyAllocation = { allocation_scope: "internal", allocation_mode: "percentage", percentage: 100, amount: 0, work_center_id: "", vendor_name: "", vendor_rfc: "", invoice_reference: "", notes: "" };

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [temporaryDialogOpen, setTemporaryDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "", position: "",
    department: "", salary: "", hire_date: new Date().toISOString().split("T")[0],
    rfc: "", curp: "", nss: "", address: "", emergency_contact: "", supervisor_id: "", gender: "", work_center_id: "",
    employment_type: "permanent", temporary_start_date: "", temporary_end_date: "", allow_overtime_request: false,
    nationality: "Mexicana", foreign_document_type: "", foreign_document_number: "",
    hierarchy_band_name: "", hierarchy_rank: 0, salary_band_min: "", salary_band_max: "",
    work_schedule: scheduleTemplate, banking_details: emptyBanking, payroll_allocations: [{ ...emptyAllocation }]
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${API}/employees`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${API}/work-centers`, { headers: h }).catch(() => ({ data: [] }))
    ]).then(([employeesRes, centersRes]) => {
      setAllEmployees(employeesRes.data || []);
      setWorkCenters(centersRes.data || []);
    }).catch(() => {});
    if (isEdit) {
      axios.get(`${API}/employees/${id}`, { headers: h }).then(res => {
        setFormData({
          ...res.data,
          salary: String(res.data.salary || ""),
          supervisor_id: res.data.supervisor_id || "",
          work_center_id: res.data.work_center_id || "",
          employment_type: res.data.employment_type || "permanent",
          allow_overtime_request: !!res.data.allow_overtime_request,
          temporary_start_date: res.data.temporary_start_date || "",
          temporary_end_date: res.data.temporary_end_date || "",
          nationality: res.data.nationality || "Mexicana",
          foreign_document_type: res.data.foreign_document_type || "",
          foreign_document_number: res.data.foreign_document_number || "",
          hierarchy_band_name: res.data.hierarchy_band_name || "",
          hierarchy_rank: res.data.hierarchy_rank || 0,
          salary_band_min: String(res.data.salary_band_min || ""),
          salary_band_max: String(res.data.salary_band_max || ""),
          work_schedule: res.data.work_schedule?.length ? res.data.work_schedule.map((item, index) => ({ ...scheduleTemplate[index], ...item })) : scheduleTemplate,
          banking_details: { ...emptyBanking, ...(res.data.banking_details || {}) },
          payroll_allocations: res.data.payroll_allocations?.length ? res.data.payroll_allocations : [{ ...emptyAllocation }]
        });
      }).catch(() => { toast.error("Error al cargar empleado"); navigate("/employees"); });
    }
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.position || !formData.department || !formData.salary) {
      toast.error("Por favor completa todos los campos requeridos"); return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const data = { ...formData, salary: parseFloat(formData.salary) };
      if (!data.supervisor_id || data.supervisor_id === "none") delete data.supervisor_id;
      if (!data.work_center_id) delete data.work_center_id;
      if (isEdit) {
        await axios.put(`${API}/employees/${id}`, data, { headers });
        toast.success("Empleado actualizado");
      } else {
        await axios.post(`${API}/employees`, data, { headers });
        toast.success("Empleado creado");
      }
      navigate("/employees");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar empleado");
    } finally { setLoading(false); }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleBankChange = (field, value) => setFormData(prev => ({ ...prev, banking_details: { ...prev.banking_details, [field]: value } }));
  const handleScheduleChange = (index, field, value) => setFormData(prev => ({ ...prev, work_schedule: prev.work_schedule.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
  const handleAllocationChange = (index, field, value) => setFormData(prev => ({ ...prev, payroll_allocations: prev.payroll_allocations.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
  const addAllocation = () => setFormData(prev => ({ ...prev, payroll_allocations: [...prev.payroll_allocations, { ...emptyAllocation, percentage: 0 }] }));
  const removeAllocation = (index) => setFormData(prev => ({ ...prev, payroll_allocations: prev.payroll_allocations.filter((_, itemIndex) => itemIndex !== index) || [{ ...emptyAllocation }] }));
  const supervisorOptions = allEmployees.filter(e => e.employee_id !== id);

  return (
    <Layout title={isEdit ? "Editar Empleado" : "Nuevo Empleado"} subtitle="Completa la información del empleado">
      <Button variant="ghost" onClick={() => navigate("/employees")} className="mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Volver a Empleados
      </Button>
      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg">Información Personal</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Nombre *</Label>
                  <Input id="first_name" value={formData.first_name} onChange={e => handleChange("first_name", e.target.value)} placeholder="Juan" data-testid="employee-first-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Apellidos *</Label>
                  <Input id="last_name" value={formData.last_name} onChange={e => handleChange("last_name", e.target.value)} placeholder="Pérez García" data-testid="employee-last-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico *</Label>
                <Input id="email" type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="juan.perez@empresa.com" data-testid="employee-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input id="phone" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+52 55 1234 5678" data-testid="employee-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Género</Label>
                  <Select value={formData.gender} onValueChange={v => handleChange("gender", v)}>
                    <SelectTrigger data-testid="employee-gender-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="femenino">Femenino</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Textarea id="address" value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="Calle, Colonia, Ciudad, CP" rows={2} data-testid="employee-address-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact">Contacto de Emergencia</Label>
                <Input id="emergency_contact" value={formData.emergency_contact} onChange={e => handleChange("emergency_contact", e.target.value)} placeholder="Nombre - Teléfono" data-testid="employee-emergency-input" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-lg">Información Laboral</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="position">Puesto *</Label>
                <Input id="position" value={formData.position} onChange={e => handleChange("position", e.target.value)} placeholder="Desarrollador Senior" data-testid="employee-position-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de contratación</Label>
                  <Select value={formData.employment_type} onValueChange={v => { handleChange("employment_type", v); if (v === "temporary") setTemporaryDialogOpen(true); }}>
                    <SelectTrigger data-testid="employee-employment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="permanent">Indefinido</SelectItem>
                      <SelectItem value="temporary">Temporal / eventual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center justify-between gap-2">Solicitar horas extra
                    <Switch checked={!!formData.allow_overtime_request} onCheckedChange={v => handleChange("allow_overtime_request", v)} data-testid="employee-overtime-switch" />
                  </Label>
                  <p className="text-xs text-slate-500">Activa o bloquea el botón de solicitud de horas extra para el colaborador.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Departamento *</Label>
                <Select value={formData.department} onValueChange={v => handleChange("department", v)}>
                  <SelectTrigger data-testid="employee-department-select"><SelectValue placeholder="Selecciona departamento" /></SelectTrigger>
                  <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Centro de Trabajo
                  <HelpTip title="Centro principal del colaborador">
                    <p>Este centro conecta asistencia, organigrama y desempeño.</p>
                    <p>Úsalo para ubicar al colaborador en su operación real.</p>
                  </HelpTip>
                </Label>
                <Select value={formData.work_center_id || "none"} onValueChange={v => handleChange("work_center_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="employee-work-center-select"><SelectValue placeholder="Selecciona un centro" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin centro asignado</SelectItem>
                    {workCenters.map(center => (
                      <SelectItem key={center.work_center_id} value={center.work_center_id}>{center.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* JEFE INMEDIATO */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><UserCog className="w-4 h-4 text-emerald-600" />Jefe Inmediato (Reporta a)</Label>
                <Select value={formData.supervisor_id || "none"} onValueChange={v => handleChange("supervisor_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="employee-supervisor-select"><SelectValue placeholder="Sin supervisor asignado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin supervisor</SelectItem>
                    {supervisorOptions.map(e => (
                      <SelectItem key={e.employee_id} value={e.employee_id}>
                        {e.first_name} {e.last_name} - {e.position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salary">Salario Mensual (MXN) *</Label>
                  <Input id="salary" type="number" value={formData.salary} onChange={e => handleChange("salary", e.target.value)} placeholder="25000" data-testid="employee-salary-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire_date">Fecha de Ingreso *</Label>
                  <Input id="hire_date" type="date" value={formData.hire_date} onChange={e => handleChange("hire_date", e.target.value)} data-testid="employee-hire-date-input" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <p className="text-sm font-medium text-slate-700">Banda jerárquica y rango salarial</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Banda</Label><Input value={formData.hierarchy_band_name || ""} onChange={e => handleChange("hierarchy_band_name", e.target.value)} placeholder="Líder, Coordinación, Gerencia" data-testid="employee-hierarchy-band-input" /></div>
                  <div className="space-y-2"><Label>Nivel jerárquico</Label><Input type="number" min="0" value={formData.hierarchy_rank ?? 0} onChange={e => handleChange("hierarchy_rank", Number(e.target.value || 0))} data-testid="employee-hierarchy-rank-input" /></div>
                  <div className="space-y-2"><Label>Banda salarial</Label><div className="grid grid-cols-2 gap-2"><Input type="number" value={formData.salary_band_min || ""} onChange={e => handleChange("salary_band_min", e.target.value)} placeholder="Desde" data-testid="employee-salary-band-min-input" /><Input type="number" value={formData.salary_band_max || ""} onChange={e => handleChange("salary_band_max", e.target.value)} placeholder="Hasta" data-testid="employee-salary-band-max-input" /></div></div>
                </div>
              </div>
              {formData.employment_type === "temporary" && (
                <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-700" data-testid="temporary-employee-summary">
                  Vigencia temporal: {formData.temporary_start_date || "sin inicio"} → {formData.temporary_end_date || "sin fin"}
                </div>
              )}
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <p className="text-sm font-medium text-slate-700">Demografía y condición migratoria</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nacionalidad</Label>
                    <Input value={formData.nationality || ""} onChange={e => handleChange("nationality", e.target.value)} placeholder="Mexicana" data-testid="employee-nationality-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Documento migratorio</Label>
                    <Input value={formData.foreign_document_type || ""} onChange={e => handleChange("foreign_document_type", e.target.value)} placeholder="FM2, residencia temporal..." data-testid="employee-foreign-document-type-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Número de documento migratorio</Label>
                  <Input value={formData.foreign_document_number || ""} onChange={e => handleChange("foreign_document_number", e.target.value)} placeholder="Número de soporte legal en México" data-testid="employee-foreign-document-number-input" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-4">Datos Fiscales (México)</p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="rfc">RFC</Label>
                    <Input id="rfc" value={formData.rfc} onChange={e => handleChange("rfc", e.target.value.toUpperCase())} placeholder="XXXX000000XXX" maxLength={13} data-testid="employee-rfc-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curp">CURP</Label>
                    <Input id="curp" value={formData.curp} onChange={e => handleChange("curp", e.target.value.toUpperCase())} placeholder="XXXX000000XXXXXX00" maxLength={18} data-testid="employee-curp-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nss">NSS (IMSS)</Label>
                    <Input id="nss" value={formData.nss} onChange={e => handleChange("nss", e.target.value)} placeholder="00000000000" maxLength={11} data-testid="employee-nss-input" />
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <p className="text-sm font-medium text-slate-700">Datos bancarios para dispersión</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Método de pago</Label>
                    <Select value={formData.banking_details?.payment_method || "transferencia"} onValueChange={v => handleBankChange("payment_method", v)}>
                      <SelectTrigger data-testid="employee-payment-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="mixto">Mixto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Banco</Label>
                    <Input value={formData.banking_details?.bank_name || ""} onChange={e => handleBankChange("bank_name", e.target.value)} placeholder="BBVA, Banorte..." data-testid="employee-bank-name-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>CLABE</Label>
                    <Input value={formData.banking_details?.clabe || ""} onChange={e => handleBankChange("clabe", e.target.value)} placeholder="18 dígitos" maxLength={18} data-testid="employee-bank-clabe-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cuenta</Label>
                    <Input value={formData.banking_details?.account_number || ""} onChange={e => handleBankChange("account_number", e.target.value)} placeholder="Cuenta" data-testid="employee-bank-account-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tarjeta</Label>
                    <Input value={formData.banking_details?.card_number || ""} onChange={e => handleBankChange("card_number", e.target.value)} placeholder="Tarjeta" data-testid="employee-bank-card-input" />
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <p className="text-sm font-medium text-slate-700">Jornada y horarios</p>
                <div className="space-y-3" data-testid="employee-schedule-editor">
                  {formData.work_schedule?.map((schedule, index) => (
                    <div key={schedule.day} className="grid grid-cols-[110px_80px_1fr_1fr] gap-3 items-center p-3 rounded-xl bg-slate-50 border border-slate-200">
                      <span className="text-sm font-medium text-slate-700">{schedule.label || schedule.day}</span>
                      <div className="flex items-center gap-2">
                        <Switch checked={!!schedule.enabled} onCheckedChange={value => handleScheduleChange(index, "enabled", value)} data-testid={`employee-schedule-toggle-${schedule.day}`} />
                        <span className="text-xs text-slate-500">{schedule.enabled ? "Labora" : "Libre"}</span>
                      </div>
                      <Input type="time" value={schedule.start_time || ""} onChange={e => handleScheduleChange(index, "start_time", e.target.value)} disabled={!schedule.enabled} data-testid={`employee-schedule-start-${schedule.day}`} />
                      <Input type="time" value={schedule.end_time || ""} onChange={e => handleScheduleChange(index, "end_time", e.target.value)} disabled={!schedule.enabled} data-testid={`employee-schedule-end-${schedule.day}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-slate-700">Centros de costo / partidas de nómina</p>
                  <Button type="button" variant="outline" size="sm" onClick={addAllocation} data-testid="employee-add-allocation-btn">Agregar partida</Button>
                </div>
                <div className="space-y-4" data-testid="employee-allocations-editor">
                  {formData.payroll_allocations?.map((allocation, index) => (
                    <div key={allocation.allocation_id || index} className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Alcance</Label>
                          <Select value={allocation.allocation_scope || "internal"} onValueChange={v => handleAllocationChange(index, "allocation_scope", v)}>
                            <SelectTrigger data-testid={`employee-allocation-scope-${index}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="internal">Interna / centro de costo</SelectItem>
                              <SelectItem value="external">Externa / factura / outsourcing</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Modo</Label>
                          <Select value={allocation.allocation_mode || "percentage"} onValueChange={v => handleAllocationChange(index, "allocation_mode", v)}>
                            <SelectTrigger data-testid={`employee-allocation-mode-${index}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Porcentaje</SelectItem>
                              <SelectItem value="fixed">Monto fijo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Centro / estación</Label>
                          <Select value={allocation.work_center_id || "none"} onValueChange={v => handleAllocationChange(index, "work_center_id", v === "none" ? "" : v)}>
                            <SelectTrigger data-testid={`employee-allocation-center-${index}`}><SelectValue placeholder="Centro" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin centro</SelectItem>
                              {workCenters.map(center => <SelectItem key={center.work_center_id} value={center.work_center_id}>{center.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>%</Label>
                          <Input type="number" value={allocation.percentage ?? 0} onChange={e => handleAllocationChange(index, "percentage", Number(e.target.value || 0))} data-testid={`employee-allocation-percentage-${index}`} />
                        </div>
                        <div className="space-y-2">
                          <Label>Monto</Label>
                          <Input type="number" value={allocation.amount ?? 0} onChange={e => handleAllocationChange(index, "amount", Number(e.target.value || 0))} data-testid={`employee-allocation-amount-${index}`} />
                        </div>
                      </div>
                      {allocation.allocation_scope === "external" && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2"><Label>Proveedor</Label><Input value={allocation.vendor_name || ""} onChange={e => handleAllocationChange(index, "vendor_name", e.target.value)} data-testid={`employee-allocation-vendor-${index}`} /></div>
                          <div className="space-y-2"><Label>RFC proveedor</Label><Input value={allocation.vendor_rfc || ""} onChange={e => handleAllocationChange(index, "vendor_rfc", e.target.value.toUpperCase())} data-testid={`employee-allocation-vendor-rfc-${index}`} /></div>
                          <div className="space-y-2"><Label>Factura / referencia</Label><Input value={allocation.invoice_reference || ""} onChange={e => handleAllocationChange(index, "invoice_reference", e.target.value)} data-testid={`employee-allocation-invoice-${index}`} /></div>
                        </div>
                      )}
                      <div className="flex justify-between gap-3 items-center">
                        <Input value={allocation.notes || ""} onChange={e => handleAllocationChange(index, "notes", e.target.value)} placeholder="Notas de la partida" data-testid={`employee-allocation-notes-${index}`} />
                        {formData.payroll_allocations.length > 1 && <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => removeAllocation(index)} data-testid={`employee-remove-allocation-${index}`}>Eliminar</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate("/employees")} data-testid="cancel-btn">Cancelar</Button>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="save-employee-btn">
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" />{isEdit ? "Guardar Cambios" : "Crear Empleado"}</>}
          </Button>
        </div>
      </form>
      <Dialog open={temporaryDialogOpen} onOpenChange={setTemporaryDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Configurar empleado temporal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Inicio de vigencia</Label>
              <Input type="date" value={formData.temporary_start_date || ""} onChange={e => handleChange("temporary_start_date", e.target.value)} data-testid="temporary-start-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Fin de vigencia</Label>
              <Input type="date" value={formData.temporary_end_date || ""} onChange={e => handleChange("temporary_end_date", e.target.value)} data-testid="temporary-end-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" className="bg-amber-600 hover:bg-amber-700" onClick={() => setTemporaryDialogOpen(false)} data-testid="temporary-save-button">Guardar vigencia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EmployeeForm;
